import { DateTime } from 'luxon';
import { z } from 'zod';
import { apiEnvelope } from '../envelope';

// ─── member medication ──────────────────────────────────────────────────────
//
// What the member app is told about the member's current prescription: one
// prescription or none. The selection rule (which row counts as current, and
// when the 1.0 snapshot stands in for live Change Healthcare data) is the
// server's; the app only renders what it is given.
//
// Client-parsed, so nothing here is `.strict()` (ADR-135 Rules 1 and 3). Which
// source answered is the server's business and is not sent.

/**
 * A real calendar date, `YYYY-MM-DD`, already resolved in the prescribing
 * facility's zone. The shape alone would admit `2026-13-45`.
 */
const calendarDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((value) => DateTime.fromISO(value, { zone: 'utc' }).isValid, 'not a calendar date');

export const memberPrescriptionSchema = z.object({
  drugName: z.string().min(1),
  prescriberName: z.string().nullable(),
  daysSupply: z.number().int().positive(),
  directions: z.string().nullable(),
  pharmacyName: z.string().nullable(),
  lastPrescribedOn: calendarDateSchema,
});
export type MemberPrescription = z.infer<typeof memberPrescriptionSchema>;

/**
 * `hasPrescription` and `prescription` must agree. A read claiming a
 * prescription it does not carry (or the reverse) is malformed, so the client
 * treats it as a failed read rather than rendering the no-prescription content
 * to a member who has one.
 */
export const memberMedicationSchema = z
  .object({
    hasPrescription: z.boolean(),
    prescription: memberPrescriptionSchema.nullable(),
  })
  .refine((read) => read.hasPrescription === (read.prescription !== null), {
    message: 'hasPrescription must match whether a prescription is present',
    path: ['hasPrescription'],
  });
export type MemberMedication = z.infer<typeof memberMedicationSchema>;

/** The `GET /members/me/medication` response envelope (ADR-002). */
export const memberMedicationResponseSchema = apiEnvelope(memberMedicationSchema);
export type MemberMedicationResponse = z.infer<typeof memberMedicationResponseSchema>;
